// src/components/results/FlowVolumeMetricsTable.jsx

import React, { memo, useMemo, useState, useCallback } from "react";
import { fmtNumber, loopIndexFromKey } from "../../utils/loopsHelpers";
import { FLOW_METRIC_COLUMNS } from "../../utils/flowMetricColumns";

function asKeyDownSelect(handler) {
  return (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler();
    }
  };
}

export const FlowVolumeMetricsTable = memo(function FlowVolumeMetricsTable({
  metrics = {},
  selectedMetric = "",
  onSelectMetric,
}) {
  const loopKeys = useMemo(
    () =>
      Object.keys(metrics).sort(
        (a, b) => loopIndexFromKey(a) - loopIndexFromKey(b),
      ),
    [metrics],
  );

  const [hoveredMetric, setHoveredMetric] = useState("");

  const canSelect = typeof onSelectMetric === "function";

  const onEnterCol = useCallback((c) => setHoveredMetric(c), []);
  const onLeaveCol = useCallback(() => setHoveredMetric(""), []);

  if (!loopKeys.length) return null;

  return (
    <div className="w-full border border-greyscale-1 rounded-lg overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-greyscale-1 border-b border-greyscale-1">
            <tr>
              <th className="text-center p-1 border-r border-greyscale-1">
                Loop
              </th>

              {FLOW_METRIC_COLUMNS.map((c) => {
                const isSel = c === selectedMetric;
                const isHovered = c === hoveredMetric;

                const select = () => onSelectMetric?.(c);

                return (
                  <th
                    key={c}
                    scope="col"
                    aria-selected={isSel ? "true" : "false"}
                    tabIndex={canSelect ? 0 : -1}
                    onClick={canSelect ? select : undefined}
                    onKeyDown={canSelect ? asKeyDownSelect(select) : undefined}
                    onMouseEnter={() => onEnterCol(c)}
                    onMouseLeave={onLeaveCol}
                    onMouseDown={
                      canSelect ? (e) => e.preventDefault() : undefined
                    }
                    className={[
                      "text-center p-2 border-r border-greyscale-1 last:border-r-0",
                      canSelect ? "cursor-pointer" : "",
                      isSel ? "flow-metric-table-head-selected" : "",
                      !isSel && isHovered ? "flow-metric-table-hovered" : "",
                      canSelect
                        ? "flow-metric-table-focus focus:outline-none focus:ring-2"
                        : "",
                    ].join(" ")}
                    title={canSelect ? "Click to plot this metric" : undefined}
                  >
                    {c}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loopKeys.map((lk) => {
              const row = metrics[lk] || {};
              const loopNum = loopIndexFromKey(lk);

              return (
                <tr
                  key={lk}
                  className="border-b border-greyscale-1 last:border-b-0"
                >
                  <td className="p-1 border-r border-greyscale-1 font-medium text-center">
                    {Number.isFinite(loopNum) ? loopNum : lk}
                  </td>

                  {FLOW_METRIC_COLUMNS.map((c) => {
                    const isSel = c === selectedMetric;
                    const isHovered = c === hoveredMetric;

                    const select = () => onSelectMetric?.(c);

                    const v = row[c];
                    const dp = c.includes("Flow")
                      ? 0
                      : c.includes("Volume")
                        ? 3
                        : 3;

                    return (
                      <td
                        key={`${lk}-${c}`}
                        aria-selected={isSel ? "true" : "false"}
                        onClick={canSelect ? select : undefined}
                        onMouseDown={
                          canSelect ? (e) => e.preventDefault() : undefined
                        } // prevent focus
                        onMouseEnter={() => onEnterCol(c)}
                        onMouseLeave={onLeaveCol}
                        className={[
                          "p-1 border-r border-greyscale-1 last:border-r-0 text-center",
                          canSelect ? "cursor-pointer" : "",
                          isSel ? "flow-metric-table-selected" : "",
                          !isSel && isHovered ? "flow-metric-table-hovered" : "",
                        ].join(" ")}
                        title={
                          canSelect ? "Click to plot this metric" : undefined
                        }
                      >
                        {fmtNumber(v, dp)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});
